import React from 'react'
import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Popover, Skeleton, Tooltip } from 'antd'
import classNames from 'classnames'
import {
  IAdminSellersOrderTimeSummary,
  IAdminSellersSummary,
  IAdminSellersSummaryApiSeller,
  TStockConfirmationSource
} from 'models/seller'
import { formatPercent, ISegmentBarItem, SegmentBar } from 'components/sellers/SegmentBar/SegmentBar'
import { SellerActivityConditions } from 'components/sellers/SellerDisableStatus/SellerDisableStatus'
import { stockConfirmationSourceLabels } from 'components/sellers/StockConfirmationStatus/utils/stockConfirmation'
import { formatMinutes } from 'shared/utils/formatMinutes'
import styles from './SellersSummary.module.scss'

interface ISellersSummaryProps {
  summary: IAdminSellersSummary | null
}

interface ISegment extends ISegmentBarItem {
  hint?: React.ReactNode
}

const ACTIVE_HINT = (
  <div className={styles.hintBody}>
    <div>Активными считаются подтверждённые продавцы, у которых выполнены все условия:</div>
    <SellerActivityConditions />
  </div>
)

const VISITED_TODAY_HINT =
  'Подтверждённые продавцы, которые сами или через сотрудников работали в кабинете сегодня (с 04:00 по Москве). Ниже — то же за последние 30 дней.'

const ORDER_TIME_HINT =
  'Учитываются только активные продавцы. У каждого берутся последние 25 заказов, затем считается среднее по продавцам — чтобы цифру не задавали магазины с большим числом заказов. Магазины, исключённые из аналитики, не учитываются.'

const CONFIRMATION_TIME_HINT = `Среднее время от оплаты заказа (статус «На подтверждении») до статуса «Принят». ${ORDER_TIME_HINT}`

const SHIPPING_TIME_HINT = `Среднее время от оплаты заказа (статус «На подтверждении») до статуса «В доставке», без самовывоза. ${ORDER_TIME_HINT}`

const STOCK_SOURCES_HINT =
  'Каким способом подтверждённые продавцы последний раз обновляли остатки. Отключённые и магазины без опубликованных товаров показаны отдельно.'

const STOCK_SOURCE_COLORS: Record<TStockConfirmationSource, string> = {
  MOYSKLAD: '#2f6fed',
  ONE_C: '#fadb14',
  EXCEL: '#1f9d61',
  MANUAL: '#8a63e8',
  API: '#13a8a8',
  INSALES: '#e0558f'
}

const MAIN_STOCK_SOURCES: TStockConfirmationSource[] = ['MOYSKLAD', 'ONE_C', 'EXCEL', 'MANUAL']
const EXTRA_STOCK_SOURCES: TStockConfirmationSource[] = ['API', 'INSALES']

const moscowDay = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit'
})
const moscowTime = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  hour: '2-digit',
  minute: '2-digit'
})

const formatConfirmedAt = (value: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  const day = moscowDay.format(date)
  const prefix = day === moscowDay.format(new Date()) ? '' : `${day} `
  return `${prefix}в ${moscowTime.format(date)} МСК`
}

const ApiSellersHint: React.FC<{ sellers: IAdminSellersSummaryApiSeller[] }> = ({ sellers }) => (
  <ul className={styles.hintList}>
    {sellers.map(({ number, shop_name, confirmed_at }) => (
      <li key={number ?? shop_name}>
        №{number} {shop_name ?? 'Без названия'} — {formatConfirmedAt(confirmed_at)}
      </li>
    ))}
  </ul>
)

const Hint: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <Tooltip title={title}>
    <QuestionCircleOutlined className={styles.hint} />
  </Tooltip>
)

const CardTitle: React.FC<{ title: string, hint: React.ReactNode }> = ({ title, hint }) => (
  <div className={styles.cardTitle}>
    {title}
    <Hint title={hint} />
  </div>
)

const InlineLegend: React.FC<{ segments: ISegment[] }> = ({ segments }) => {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <div className={styles.details}>
      <Popover
        trigger='click'
        placement='bottomLeft'
        open={isOpen}
        onOpenChange={setIsOpen}
        content={
          <div className={styles.inlineLegend}>
            {segments.map(({ key, label, value, color }) => (
              <span key={key} className={styles.inlineLegendItem}>
                <span className={styles.dot} style={{ background: color }} />
                {label}: {value}
              </span>
            ))}
          </div>
        }
      >
        <button type='button' className={styles.detailsToggle} aria-expanded={isOpen}>
          Подробнее
          <DownOutlined className={classNames(styles.detailsIcon, isOpen && styles.detailsIconOpen)} />
        </button>
      </Popover>
    </div>
  )
}

const OrderTimeCard: React.FC<{
  title: string
  hint: string
  time: IAdminSellersOrderTimeSummary
}> = ({ title, hint, time }) => (
  <div className={styles.card}>
    <CardTitle title={title} hint={hint} />
    <div className={styles.value}>
      {time.average_minutes === null ? '—' : formatMinutes(time.average_minutes)}
    </div>
    <div className={styles.meta}>
      {time.sellers_count
        ? `магазинов: ${time.sellers_count} · заказов: ${time.orders_count}`
        : 'нет заказов с отметками'}
    </div>
  </div>
)

const SummarySkeleton: React.FC = () => (
  <div className={styles.grid}>
    {[0, 1, 2, 3].map((key) => (
      <div key={key} className={styles.card}>
        <Skeleton active title={{ width: '50%' }} paragraph={{ rows: 2, width: ['70%', '40%'] }} />
      </div>
    ))}
    <div className={classNames(styles.card, styles.stockCard)}>
      <Skeleton active title={{ width: '25%' }} paragraph={{ rows: 2, width: ['100%', '60%'] }} />
    </div>
  </div>
)

export const SellersSummary: React.FC<ISellersSummaryProps> = ({ summary }) => {
  if (!summary) return <SummarySkeleton />

  const approved = summary.approved_count

  const activitySegments: ISegment[] = [
    { key: 'active', label: 'Активные', value: summary.active_count, color: '#3fb950' },
    { key: 'noProducts', label: 'Без товаров', value: summary.enabled_no_products_count, color: '#b4bfcf' },
    { key: 'noVisits', label: 'Не заходили 30 дней', value: summary.enabled_no_visits_count, color: '#7c8db5' },
    { key: 'outdated', label: 'Отключены за остатки', value: summary.stock_outdated_count, color: '#f59f00' },
    {
      key: 'manual',
      label: 'Отключены вручную',
      value:
        summary.disabled_count -
        summary.disabled_for_cancels_count -
        summary.disabled_profile_incomplete_count,
      color: '#f0525e'
    },
    { key: 'cancels', label: 'Отключены за отмены', value: summary.disabled_for_cancels_count, color: '#2f6fed' },
    {
      key: 'profile',
      label: 'Профиль не заполнен',
      value: summary.disabled_profile_incomplete_count,
      color: '#9c36b5'
    }
  ]

  const stockSegments: ISegment[] = [
    ...MAIN_STOCK_SOURCES,
    ...EXTRA_STOCK_SOURCES.filter((source) => summary.stock_sources[source] > 0)
  ].map((source) => ({
    key: source,
    label: stockConfirmationSourceLabels[source],
    value: summary.stock_sources[source] ?? 0,
    color: STOCK_SOURCE_COLORS[source],
    hint:
      source === 'API' && summary.api_sellers?.length
        ? <ApiSellersHint sellers={summary.api_sellers} />
        : undefined
  }))
  // Не отключён и ни разу не обновлял — только новый магазин до истечения срока
  if (summary.stock_never_updated_count > 0) {
    stockSegments.push({
      key: 'never',
      label: 'Ни разу не обновляли',
      value: summary.stock_never_updated_count,
      color: '#f59f00'
    })
  }
  stockSegments.push(
    {
      key: 'disabled',
      label: 'Отключены',
      value: summary.stock_disabled_count,
      color: '#f0525e'
    },
    {
      key: 'noProducts',
      label: 'Магазины без товаров',
      value: summary.no_products_count,
      color: '#b4bfcf'
    }
  )

  return (
    <div className={styles.grid}>
      <div className={styles.card}>
        <CardTitle title='Активные' hint={ACTIVE_HINT} />
        <div className={styles.value}>
          {summary.active_count}
          <span className={styles.valueTotal}>из {approved}</span>
        </div>
        <SegmentBar className={styles.bar} segments={activitySegments} total={approved} />
        <InlineLegend segments={activitySegments.slice(1)} />
      </div>

      <div className={styles.card}>
        <CardTitle title='Заходили сегодня' hint={VISITED_TODAY_HINT} />
        <div className={styles.value}>
          {summary.visited_today_count}
          <span className={styles.valueTotal}>из {approved}</span>
        </div>
        <div className={styles.meta}>
          Заходили за 30 дней: {summary.visited_30_days_count} из {approved}
        </div>
      </div>

      <OrderTimeCard
        title='Подтверждение заказа'
        hint={CONFIRMATION_TIME_HINT}
        time={summary.confirmation_time}
      />
      <OrderTimeCard
        title='Отправка заказа'
        hint={SHIPPING_TIME_HINT}
        time={summary.shipping_time}
      />

      <div className={classNames(styles.card, styles.stockCard)}>
        <CardTitle title='Обновление остатков' hint={STOCK_SOURCES_HINT} />
        <SegmentBar className={styles.bar} segments={stockSegments} total={approved} />
        <div className={styles.legend}>
          {stockSegments.map(({ key, label, value, color, hint }) => (
            <div
              key={key}
              className={classNames(styles.legendItem, !value && styles.legendItemEmpty)}
            >
              <span className={styles.dot} style={{ background: color }} />
              <span className={styles.legendLabel}>{label}</span>
              <span className={styles.legendValue}>{value}</span>
              <span className={styles.legendPercent}>{formatPercent(value, approved)}</span>
              {hint && <Hint title={hint} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
